import { Box } from '@mui/material'
import PageContainer from 'layouts/PageContainer'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import Preloader from 'ui/Preloader'
import { DashboardGrid } from './components/DashboardGrid/DashboardGrid'

import type { DropAnimation } from '@dnd-kit/core'
import {
	defaultDropAnimationSideEffects,
	DndContext,
	DragOverlay,
} from '@dnd-kit/core'
import {
	fetchWidgetSettings,
	initializeWidgetsWithLicenses,
	updateDashboardRows,
} from 'store/dashboardSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import {
	hideDigitizationUi,
	isDigitizationWidgetType,
} from 'utils/companyDigitizationFeatures'
import {
	isMilkYieldWidgetType,
	showMilkYieldUi,
} from 'utils/companyMilkFeatures'
import { GAP_SIZE } from './components/DashboardGrid/constants'
import { WidgetContainer } from './components/DashboardGrid/styles'
import { renderWidgetContent } from './components/DashboardGrid/utils/renderWidgetContent'
import WidgetDock from './components/WidgetDock/WidgetDock'
import { calculateColumns, getWidgetDimensions } from './constants/constants'

const dropAnimation: DropAnimation = {
	sideEffects: defaultDropAnimationSideEffects({
		styles: { active: { opacity: '0.5' } },
	}),
	duration: 280,
	easing: 'cubic-bezier(0.2, 0, 0, 1)',
}

import { usePageContainerWidth } from './components/DashboardGrid/hooks/usePageContainerWidth'
import { useDashboardDataPrefetch } from './hooks/useDashboardDataPrefetch'
import { useDashboardDnd } from './hooks/useDashboardDnd'
import { WidgetConfig } from './types'
import { rowInsertFirstCollision } from './utils/collisionDetection'
import { groupWidgetsIntoRows, removeEmptyRows } from './utils/widgetRowUtils'

export const FarmDashboard: React.FC = () => {
	const { widgets, rows, isSettingsLoading } = useAppSelector(
		state => state.dashboardSlice,
	)
	const { permissions } = useAppSelector(state => state.authReducer)
	const { userData } = useAppSelector(state => state.userReducer)
	const dispatch = useAppDispatch()
	const pageContainerRef = useRef<HTMLDivElement>(null)
	const pageContainerWidth = usePageContainerWidth(pageContainerRef)
	const allowMilkYield = showMilkYieldUi(userData?.company?.id)
	const hideDigitization = hideDigitizationUi(userData?.company?.id)

	useEffect(() => {
		dispatch(fetchWidgetSettings())
	}, [dispatch])

	useEffect(() => {
		dispatch(initializeWidgetsWithLicenses(permissions))
	}, [dispatch, permissions])

	// Миграция: если rows пустые, но есть widgets - конвертируем
	useEffect(() => {
		if (rows.length === 0 && widgets.length > 0 && pageContainerWidth > 0) {
			const columnsCount = calculateColumns(pageContainerWidth, GAP_SIZE)
			const convertedRows = groupWidgetsIntoRows(widgets, columnsCount)
			if (convertedRows.length > 0) {
				dispatch(updateDashboardRows(convertedRows))
			}
		}
	}, [widgets, rows.length, pageContainerWidth, dispatch])

	// Используем rows, если они есть, иначе создаем из widgets
	const dashboardRows = useMemo(() => {
		let nextRows =
			rows.length > 0
				? rows
				: widgets.length > 0
					? groupWidgetsIntoRows(
							widgets,
							calculateColumns(pageContainerWidth || 1200, GAP_SIZE),
						)
					: []

		if (!allowMilkYield || hideDigitization) {
			nextRows = removeEmptyRows(
				nextRows.map(row => ({
					...row,
					widgets: row.widgets.filter(widget => {
						if (!allowMilkYield && isMilkYieldWidgetType(widget.type)) {
							return false
						}
						if (hideDigitization && isDigitizationWidgetType(widget.type)) {
							return false
						}
						return true
					}),
				})),
			)
		}

		return nextRows
	}, [rows, widgets, pageContainerWidth, allowMilkYield, hideDigitization])

	const handleRowsUpdate = useCallback(
		(updatedRows: typeof dashboardRows) => {
			dispatch(updateDashboardRows(updatedRows))
		},
		[dispatch],
	)

	const handleReorderWidgetsInRow = useCallback(
		(rowId: string, reorderedWidgets: WidgetConfig[]) => {
			const updatedRows = dashboardRows.map(row =>
				row.id === rowId ? { ...row, widgets: reorderedWidgets } : row,
			)
			dispatch(updateDashboardRows(updatedRows))
		},
		[dashboardRows, dispatch],
	)

	const handleDropWidget = useCallback(() => {
		// Эта функция вызывается из RowContainer, но логика уже обработана в useDashboardDnd
		// Оставляем пустой или можем добавить дополнительную логику
	}, [])

	const {
		sensors,
		handleDragStart,
		handleDragEnd,
		activeId,
		activeDockWidget,
		activeWidget,
	} = useDashboardDnd({ rows: dashboardRows, onUpdateRows: handleRowsUpdate })

	const { isReady } = useDashboardDataPrefetch()

	return (
		<PageContainer>
			<div ref={pageContainerRef}>
				{!isReady || isSettingsLoading ? (
					<Box
						display='flex'
						justifyContent='center'
						minHeight={200}
						alignItems='center'
					>
						<Preloader />
					</Box>
				) : (
					<DndContext
						sensors={sensors}
						collisionDetection={rowInsertFirstCollision}
						onDragStart={handleDragStart}
						onDragEnd={handleDragEnd}
					>
						<DashboardGrid
							rows={dashboardRows}
							activeId={activeId}
							pageContainerRef={pageContainerRef}
							onReorderWidgetsInRow={handleReorderWidgetsInRow}
							onDropWidget={handleDropWidget}
						/>
						<WidgetDock />
						<DragOverlay dropAnimation={dropAnimation}>
							{activeDockWidget ? (
								<WidgetContainer
									w={getWidgetDimensions(activeDockWidget.size).w}
									h={getWidgetDimensions(activeDockWidget.size).h}
									style={{ opacity: 0.95, zIndex: 10000, cursor: 'grab' }}
								>
									{renderWidgetContent(activeDockWidget)}
								</WidgetContainer>
							) : activeWidget ? (
								<WidgetContainer
									w={getWidgetDimensions(activeWidget.size).w}
									h={getWidgetDimensions(activeWidget.size).h}
									style={{
										opacity: 0.85,
										zIndex: 10000,
										pointerEvents: 'none',
									}}
								>
									{renderWidgetContent(activeWidget)}
								</WidgetContainer>
							) : null}
						</DragOverlay>
					</DndContext>
				)}
			</div>
		</PageContainer>
	)
}
